import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { L10nTranslationService } from 'angular-l10n';
import { concat, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpMethods } from '../../shared/utils/http-methods.class';
import { StackingPlanService } from '../../stacking-plan.service';
import { StackingPlanConfiguration, STACKING_PLAN_CONFIGS_TOKEN } from '../../stacking-plan.token';
import {
  ReqDeleteNonRentableSpace,
  ReqUpdateNonRentableSpace,
} from '../dialogs/non-rentable-space/non-rentable-space.model';
import { DeleteFloorPlanEDPReq, updateFloorEdpReq } from '../floor-container/floor/floor.model';
import { DetailRequestModel } from '../floor-container/floor/item/details-overlay/request/abstract-request.class';
import {
  DeleteElevatorEDPRes,
  FloorUpdateAction,
  ReqCreateElevatorInfo,
  ReqDeleteElevators,
  ReqFloorInfo,
  ReqFloorPlan,
  ReqPropertyInfo,
  ReqUpdateElevatorInfo,
  UpdateElevatorEdpRes,
} from './actions-toolbar.model';
import { FloorClass } from '../../models/floor.class';
import { HttpStrategies, HttpStreamsService } from '@cbre/http';
import { EntityType } from '@miq/domain';
import { LovValues } from '../../stacking-plan.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

function writeAPIFactory(
  writeType: 'write' | 'multiwrite',
  entityID: number,
  method: string,
  entity: string = EntityType.Property
) {
  return `/${writeType}?entity=${entity}&id=${entityID}&method=${method}`;
}

@Injectable({
  providedIn: 'root',
})
export class ActionsToolbarService extends HttpMethods {
  private readonly logger: CbreLogger = inject(CbreLoggerToken);
  constructor(
    httpClient: HttpClient,
    @Inject(STACKING_PLAN_CONFIGS_TOKEN) spConfigs: StackingPlanConfiguration,
    private stackingPlanService: StackingPlanService,
    private translationService: L10nTranslationService,
    private snackbarService: MatSnackBar,
    private httpStreamsService: HttpStreamsService
  ) {
    super(spConfigs.baseUrl, httpClient);
  }

  public uploadFloorPlan(file: FormData): Observable<any> {
    const uri = `/media?query=stackingplan`;
    return this.httpPost(uri, file) as Observable<any>;
  }

  public deleteFloorPlan(deleteFloorPlans: ReqFloorPlan[]): Observable<DeleteFloorPlanEDPReq> {
    const edpMethod = 'deleteFloorPlan';
    return this.httpPut(
      writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, edpMethod),
      deleteFloorPlans
    ) as Observable<DeleteFloorPlanEDPReq>;
  }

  public updateFloorsInfo(
    floorUpdates: ReqFloorInfo[] | ReqUpdateNonRentableSpace[],
    updateAction: FloorUpdateAction = 'floor-info'
  ): Observable<updateFloorEdpReq> {
    let response: Observable<updateFloorEdpReq> = of(null);
    if (floorUpdates != null && floorUpdates.length > 0) {
      const edpMethodsMap: Record<FloorUpdateAction, string> = {
        'floor-info': 'addPropertySpace',
        'floor-info-bulk': 'updatePropertySpace',
        'floor-number': 'addPropertySpace,updateFloorNumber',
      };
      const edpMethod = edpMethodsMap[updateAction];
      response = this.httpPut(
        writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, edpMethod),
        floorUpdates
      ) as Observable<updateFloorEdpReq>;
    }
    return response;
  }

  public createElevatorBank(newElevatorBanks: ReqCreateElevatorInfo[]): Observable<UpdateElevatorEdpRes> {
    const edpMethod = 'addElevatorBank';
    return this.httpPut(
      writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, edpMethod),
      newElevatorBanks
    ) as Observable<UpdateElevatorEdpRes>;
  }

  public updateElevatorInfoToEDP(elevatorUpdates: ReqUpdateElevatorInfo[]): Observable<UpdateElevatorEdpRes> {
    const edpMethod = 'updateElevatorBank';
    return this.httpPut(
      writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, edpMethod),
      elevatorUpdates
    ) as Observable<UpdateElevatorEdpRes>;
  }

  public updateUndergroundFloors(reqUndergroundFloors: ReqPropertyInfo) {
    const edpMethod = 'updateProperty';
    return this.httpPut(writeAPIFactory('write', this.stackingPlanService.propertyId, edpMethod), reqUndergroundFloors);
  }

  public deleteElevatorInfo(deleteElevators: ReqDeleteElevators[]): Observable<DeleteElevatorEDPRes> {
    const edpMethod = 'deleteElevatorBank';
    return this.httpPut(
      writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, edpMethod),
      deleteElevators
    ) as Observable<DeleteElevatorEDPRes>;
  }

  public saveNonRentableData(nonRentableSpaceRequest: ReqUpdateNonRentableSpace): Observable<updateFloorEdpReq> {
    return this.updateFloorsInfo([nonRentableSpaceRequest]);
  }

  public deleteNonRentableData(deleteNRS: ReqDeleteNonRentableSpace) {
    const edpMethod = 'deleteNonRentableSpace';
    return this.httpPut(writeAPIFactory('write', this.stackingPlanService.propertyId, edpMethod), deleteNRS);
  }

  public getSpaceNameLOVs(): Observable<LovValues[]> {
    return this.httpGet('/lookup/ref?query=space_classification');
  }

  public getAreaLOVs(disableCache = false): Observable<LovValues[]> {
    return this.httpStreamsService.getStream(
      '/lookup/ref/country?lookupcategory=area',
      HttpStrategies.GET,
      disableCache
    );
  }

  public getDistanceLOVs(disableCache = false): Observable<LovValues[]> {
    return this.httpStreamsService.getStream(
      '/lookup/ref/country?lookupcategory=distance',
      HttpStrategies.GET,
      disableCache
    );
  }

  public getElevatorBankCapacityLOVs(disableCache = false): Observable<LovValues[]> {
    return this.httpStreamsService.getStream(
      '/lookup/ref/country?lookupcategory=weight',
      HttpStrategies.GET,
      disableCache
    );
  }

  public getElevatorTypes(disableCache = false): Observable<LovValues[]> {
    return this.httpStreamsService.getStream(
      '/lookup/ref/country?lookupcategory=miq_elevator_type',
      HttpStrategies.GET,
      disableCache
    );
  }

  public getADACompliants(disableCache = false): Observable<LovValues[]> {
    return this.httpStreamsService.getStream('/lookup/yn', HttpStrategies.GET, disableCache);
  }

  public errorHandling(message: string) {
    const close = this.translationService.translate('commonLabels.close');
    const translatedMessage = this.translationService.translate(message);
    return catchError(err => {
      this.snackbarService.open(translatedMessage, close, { duration: 2000 });
      this.logger.error(`Error handling stacking plan request: ${err}`);
      return of('error');
    });
  }

  public updateOverlapFormData(
    detailPayloads: DetailRequestModel[],
    entity: string,
    entityID: number
  ): Observable<any> {
    if (detailPayloads.length > 1) {
      return this.getUpdateMultipleOverlap(detailPayloads, entity, entityID);
    }
    return this.getUpdateSingleOverlap(detailPayloads[0], entity, entityID);
  }

  private getUpdateMultipleOverlap(
    detailPayloads: DetailRequestModel[],
    entity: string,
    entityID: number
  ): Observable<any> {
    const updateRequests$ = detailPayloads.map((detailPayload: DetailRequestModel) =>
      this.getUpdateSingleOverlap(detailPayload, entity, entityID)
    );
    return concat(...updateRequests$).pipe(catchError(() => of()));
  }

  private getUpdateSingleOverlap(detailPayload: DetailRequestModel, entity: string, entityID: number): Observable<any> {
    const write = writeAPIFactory('write', entityID, detailPayload.method, entity);
    return this.httpPut<any>(write, detailPayload.payload);
  }

  public saveStackingPlanUpdate(floorInfo: FloorClass[]): Observable<DeleteFloorPlanEDPReq> {
    const deleteFloorPlans = floorInfo.map(floor_det => {
      return {
        floor_plan_id: floor_det.floorPlanId,
        floor_id: floor_det.floorId,
        'request.source_id': this.stackingPlanService.propertyId,
        'property_space.floor_index': floor_det.floorIndex,
        floor_number: floor_det.floorNumber,
        property_id: this.stackingPlanService.propertyId,
        floor_index: floor_det.floorIndex,
      };
    });
    return this.httpPut(
      writeAPIFactory('multiwrite', this.stackingPlanService.propertyId, 'expireFloor'),
      deleteFloorPlans
    ) as Observable<DeleteFloorPlanEDPReq>;
  }
}
